import { ClearOutlined, SearchOutlined } from '@ant-design/icons';
import { FilterProductProps } from '@features/products/interfaces/productInterface';
import { Button, Col, Flex, Input, Select, Tooltip } from 'antd';
import { FC, useEffect, useState } from 'react';

import { useDebounce } from 'src/app/hooks/useDebounce';

const STATUS_OPTIONS = {
  true: 'Ativo',
  false: 'Inativo',
};

const TIMEOUT_DEBOUNCE = 900;

export const FilterProducts: FC<FilterProductProps> = ({
  initialFilterData,
  onChangeFilter,
  profiles,
  providers,
}) => {
  const [searchFilter, setSearchFilter] = useState(
    initialFilterData?.search || ''
  );
  const [warrantyFilter, setWarrantyFilter] = useState(
    initialFilterData?.warrantyDays || ''
  );
  const [filters, setFilters] = useState<Record<string, any>>(
    initialFilterData || {}
  );

  const searchFilterDebounce = useDebounce(
    searchFilter ?? '',
    TIMEOUT_DEBOUNCE
  );
  const warrantyFilterDebounce = useDebounce(
    warrantyFilter ?? '',
    TIMEOUT_DEBOUNCE
  );

  const updateFilter = (key: string, value: any) => {
    setFilters(prev => {
      const newFilters = { ...prev };
      if (value === undefined || value === null || value === '') {
        delete newFilters[key];
      } else {
        newFilters[key] = value;
      }
      return newFilters;
    });
  };

  useEffect(() => {
    const filtersToSend = { ...filters };

    if (!searchFilterDebounce) {
      delete filtersToSend.search;
    } else {
      filtersToSend.search = searchFilterDebounce;
    }

    if (!warrantyFilterDebounce || isNaN(Number(warrantyFilterDebounce))) {
      delete filtersToSend.warrantyDays;
    } else {
      filtersToSend.warrantyDays = Number(warrantyFilterDebounce);
    }

    onChangeFilter(filtersToSend);
  }, [searchFilterDebounce, warrantyFilterDebounce, filters]);

  const handleClearAllFilters = () => {
    setSearchFilter('');
    setWarrantyFilter('');
    setFilters({});
  };

  return (
    <Flex gap={12} style={{ marginBottom: '4px' }} wrap>
      <Col flex={'auto'}>
        <Input
          style={{ flex: 1 }}
          placeholder={'Pesquisar por id ou nome'}
          suffix={<SearchOutlined />}
          value={searchFilter}
          onChange={value => {
            setSearchFilter(value.target.value);
          }}
          allowClear
        />
      </Col>
      <Col flex={'0 350px'}>
        <Select
          style={{ width: '100%' }}
          placeholder={'Profile'}
          optionFilterProp='label'
          value={!!filters.profileId ? String(filters.profileId) : undefined}
          options={profiles}
          onChange={value => {
            updateFilter('profileId', !!value ? Number(value) : undefined);
          }}
          allowClear
          showSearch
        />
      </Col>
      <Col flex={'0 150px'}>
        <Input
          style={{ width: '100%' }}
          placeholder={'Garantia'}
          value={warrantyFilter}
          onChange={value => {
            setWarrantyFilter(value.target.value);
          }}
          allowClear
        />
      </Col>
      <Col flex={'0 150px'}>
        <Select
          style={{ width: '100%' }}
          placeholder={'Adquirente'}
          optionFilterProp='label'
          value={!!filters.providerId ? String(filters.providerId) : undefined}
          options={providers}
          onChange={value => {
            updateFilter('providerId', !!value ? Number(value) : undefined);
          }}
          filterSort={(optionA, optionB) =>
            optionA.label.localeCompare(optionB.label)
          }
          allowClear
          showSearch
        />
      </Col>
      <Col flex={'150px'} style={{ maxWidth: 150 }}>
        <Select
          style={{ width: '100%' }}
          placeholder={'Status'}
          value={filters.status}
          options={Object.keys(STATUS_OPTIONS).map(key => ({
            value: key,
            label: STATUS_OPTIONS[key as keyof typeof STATUS_OPTIONS],
          }))}
          onChange={value => {
            updateFilter('status', value);
          }}
          allowClear
        />
      </Col>
      <Col>
        <Tooltip title={'Limpar filtros'}>
          <Button onClick={handleClearAllFilters}>
            <ClearOutlined />
          </Button>
        </Tooltip>
      </Col>
    </Flex>
  );
};
